<style lang="less" scoped src="@/assets/less/manage-space/base.less"></style>
<template>
    <div>
        <!-- search and actions start -->
        <div class="d-flex align-items-center justify-content-between">
            <CoInput
                v-model="search"
                type="search"
                :placeholder="$t('placeholders.search')"
                class="mr-2"
                icon="search"
                no-border
                background="white"
            ></CoInput>

            <!-- sync available keys/tags button -->
            <div class="d-flex align-items-center">
                <CoButton
                    variant="secondary"
                    role="button"
                    :title="$t('labels.keyimport')"
                    @click="importkeys()"
                    class="mr-2"
                    :loading="loading"
                >
                    {{ $t('labels.keyimport') }}
                </CoButton>
                <CoButton
                    :loading="loading"
                    variant="primary"
                    role="button"
                    :title="$t('labels.keycreate')"
                    @click="createAccessIdentity()"
                >
                    {{ $t('labels.create') }}
                </CoButton>
            </div>
        </div>
        <!-- search and actions end -->

        <CoTable
            :loading="loading"
            :itemsPerPage="10"
            :items="filteredItems"
            resizable-columns
            auto-items-per-page
            :columns="[
                { key: 'id', title: 'ID', sortable: true },
                { key: 'username', title: 'Name', sortable: true },
                { key: 'object.Email', title: 'Email', sortable: true },
                { key: 'accesskey', title: 'Access Key', sortable: true },
                { key: 'policies', title: 'Policies', sortable: true },
            ]"
            clickable-rows
            @clickedRow="editeAccessIdentity"
        >
        </CoTable>

        <!-- Access Identity Create edit Modal -->
        <CoModal
            ref="accessIdentityModal"
            size="md"
            :title="$t('labels.keymanagement')"
            hide-footer
            centered
            @close="accessKeyModalClosed"
        >
            <template v-slot:body>
                <IdentityCreateEdit
                    :identity="currentIdentity"
                    @identity-deleted="accessChangesSaved"
                    @identity-updated="accessChangesSaved"
                    @identity-create-error="accessChangesFailed"
                    @identity-update-error="accessChangesFailed"
                    @identity-delete-error="accessChangesFailed"
                    @cancel="cancelAccessKeyChanges"
                />
            </template>
        </CoModal>
    </div>
</template>

<script>
import axios from 'axios';
import Vue from 'vue';
import EventBus from '@/eventBus';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import CoTable from '@/components/Molecules/co-table/CoTable.vue';
import { get } from 'lodash';
import IdentityCreateEdit from '@/components/Templates/Admin/Access/IdentityCreateEdit/IdentityCreateEdit.vue';

export default {
    name: 'AccessKeyList',
    components: { CoButton, CoInput, CoTable, IdentityCreateEdit },
    data() {
        return {
            loading: false,
            objects: [],
            search: '',

            currentIdentity: null,
        };
    },
    mounted() {
        this.list();
    },
    computed: {
        filteredItems() {
            if (!this.search) {
                return this.objects;
            }
            const searchLowerCase = this.search.toLowerCase();
            return this.objects.filter((item) => {
                if (
                    item.object.DisplayName &&
                    item.object.DisplayName.toLowerCase().indexOf(searchLowerCase) > -1 == true
                ) {
                    return true;
                }
                if (
                    item.object.FirstName &&
                    item.object.FirstName.toLowerCase().indexOf(searchLowerCase) > -1 == true
                ) {
                    return true;
                }
                if (item.object.LastName && item.object.LastName.toLowerCase().indexOf(searchLowerCase) > -1 == true) {
                    return true;
                }
                if (item.object.Email && item.object.Email.toLowerCase().indexOf(searchLowerCase) > -1 == true) {
                    return true;
                }

                const SystemUserID =
                    String(item.object.SystemUserID || item.object.SystemUserID2)
                        .toLowerCase()
                        .indexOf(searchLowerCase) > -1;
                if (SystemUserID) {
                    return true;
                }

                if (
                    item.object.Tags &&
                    item.object.Tags.some((tag) => tag.Name.toLowerCase().indexOf(searchLowerCase) > -1)
                ) {
                    return true;
                }
                if (
                    item.object.Policies &&
                    item.object.Policies.some((policy) => policy.Name.toLowerCase().indexOf(searchLowerCase) > -1)
                ) {
                    return true;
                }
                return false;
            });
        },
    },
    methods: {
        createAccessIdentity() {
            this.currentIdentity = {};
            this.$refs.accessIdentityModal.show();
        },
        editeAccessIdentity(index, identity) {
            if (!identity) {
                return;
            }
            this.currentIdentity = identity.object;
            this.$refs.accessIdentityModal.show();
        },
        accessKeyModalClosed() {
            this.currentIdentity = {};
        },
        cancelAccessKeyChanges() {
            this.$refs.accessIdentityModal.hide();
            this.currentIdentity = {};
        },
        accessChangesSaved() {
            EventBus.$emit('INFO', {
                Message: this.$t('messages.changessaved'),
                Details: '',
            });
            this.$refs.accessIdentityModal.hide();
            // replace current identity in the list
            this.objects = [];
            this.list();
            this.currentIdentity = {};
        },
        accessChangesFailed(error) {
            let errMsg = this.$t('errors.failedGeneric');

            const customErrorCode = get(error, 'response.data.code', null);
            if (customErrorCode) {
                errMsg = this.$t(`errors.accessManagement.${customErrorCode}`) || errMsg;
            }
            EventBus.$emit('ERROR', {
                Message: errMsg,
                Details: '',
            });
        },
        getAccessKeyName(object) {
            if (object.Tags && object.Tags.length > 0) {
                return object.Tags.map((tag) => tag.Name).join(', ');
            }
            return this.$t('labels.keynotassigned');
        },

        getAccessPolicies(object) {
            if (object.Policies && object.Policies.length > 0) {
                return object.Policies.map((policy) => policy.Name).join(', ');
            }
            return this.$t('labels.policynotassigned');
        },
        getUserName(object) {
            if (object.DisplayName) {
                return object.DisplayName;
            }

            if (object.FirstName && object.LastName) {
                return `${object.FirstName} ${object.LastName}`;
            }

            if (object.FirstName) {
                return object.FirstName;
            }

            if (object.LastName) {
                return object.LastName;
            }

            return '-';
        },
        importkeys() {
            this.loading = true;
            axios
                .get('/admin/space/access/tag/import')
                .then((response) => {
                    if (response && response.data) {
                        this.list();
                    }
                    this.loading = false;
                })
                .catch((error) => {
                    console.log(error);
                    this.loading = false;
                });
        },
        list(url) {
            let defaultUrl = '/admin/space/access/identity/list';
            if (url) {
                defaultUrl = url;
            }
            this.loading = true;
            axios
                .get(defaultUrl)
                .then((response) => {
                    if (response && response.data) {
                        let data = response.data.objects || [];
                        data = data.map((item) => ({
                            ...item,
                            id: item.object.SystemUserID || item.object.SystemUserID2,
                            accesskey: this.getAccessKeyName(item.object),
                            policies: this.getAccessPolicies(item.object),
                            username: this.getUserName(item.object),
                        }));

                        this.objects.push(...data);
                        if (response.data.next) {
                            this.list(response.data.next);
                        }
                    }
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>
